import { type ReadonlyVec2 } from 'gl-matrix';

import { colorDefinitionToColor, GraphicStrokeColor, GraphicFillColor } from './color.ts';
import { GraphicLruCache } from './graphic-lru-cache.ts';
import { graphicFillToPath2D } from './graphic-svg-transformer.ts';
import { getSectionAsLines } from './math.ts';
import { GraphicPoint, type ReadonlyGraphicPoint } from './points.ts';
import {
  type GraphicLine,
  GraphicSection,
  type GraphicSectionDefinition,
  segmentDefinitionToSegment,
  GraphicFillSection,
  type GraphicFillSectionDefinition,
} from './types.ts';

export {
  GraphicSolidColor,
  GraphicStrokeColor,
  GraphicFillColor,
  GraphicLinearGradient,
  GraphicRadialGradient,
} from './color.ts';
export type {
  GradientStop,
  GraphicColorDefinition,
  GraphicSolidColorDefinition,
  GraphicStrokeColorDefinition,
  GraphicFillColorDefinition,
  GraphicLinearGradientDefinition,
  GraphicRadialGradientDefinition,
} from './color.ts';
export { GraphicSubPathStart, GraphicLine, GraphicCubicBezier } from './types.ts';
export type { GraphicSection, GraphicFillSection } from './types.ts';

export type OnChangeCallback = (graphic: Graphic) => void;

export type GraphicParams = {
  /**
   * The fills and strokes that make up the Graphic.
   * This is a JSON-like structure - see {@link GraphicContent} for the format.
   * To build this in a human-writable way, see {@link file://./graphic-builder.ts}.
   */
  content: GraphicContent;
  /**
   * The natural size of this graphic in world units.
   * This usually functions as the graphics initial board size.
   * i.e. the size that this graphic is considered to be not squashed or stretched.
   */
  naturalSize: ReadonlyVec2;
};

/**
 * A collection of fills and strokes making up a Graphic. Has the form
 * ```
 * {
 *   fills: [
 *     {
 *       segments: [
 *         { type: 'line', start: [x, y], end: [x, y] },
 *         { type: 'cubicBezier', start: [x, y], cp1: [x, y], cp2: [x, y], end: [x, y] },
 *       ]
 *     }
 *   ],
 *   strokes: [
 *     {
 *       segments: [
 *         { type: 'line', start: [x, y], end: [x, y] },
 *         { type: 'cubicBezier', start: [x, y], cp1: [x, y], cp2: [x, y], end: [x, y] },
 *       ]
 *     }
 *   ],
 *   textArea?: {
 *     topLeft: [x, y],
 *     bottomRight: [x, y],
 *   },
 * }
 * ```
 * For a properly defined fill/stroke, each segment's start point should match
 * the previous segment's end point. Additionally, for a properly defined fill,
 * the last segment's end point should match the first segment's start point.
 *
 * This is the JSON-like definition of Graphic content. For a more
 * human-writable interface, see {@link file://./graphic-builder.ts}.
 */
export type GraphicContent = {
  fills: GraphicFillSectionDefinition[];
  strokes: GraphicSectionDefinition[];
  /**
   * Defines the rectangular text area bounds in graphic-space coordinates.
   */
  textArea?: { topLeft: GraphicPoint; bottomRight: GraphicPoint };
};

/**
 * A canvas-specific data structure for defining vector shapes that can be rendered using WebGL and SVG,
 * and also provides geometric operations like hit tests and points along outline.
 * It leans heavily on SVG path concepts while also adding some features to better support WebGL rendering.
 * it's API is intended to be somewhat similar to [Path2D](https://developer.mozilla.org/en-US/docs/Web/API/Path2D).
 */
export class Graphic {
  /**
   * The natural size of this graphic in world units.
   * i.e. the size that this graphic is considered to be not squashed or stretched.
   *
   * Defaults to [1,1]
   */
  readonly naturalSize: ReadonlyVec2;
  readonly naturalAspectRatio: number;

  readonly fills: GraphicFillSection[] = [];
  readonly strokes: GraphicSection[] = [];
  readonly textArea: GraphicTextArea;

  private readonly fillCount: number = 0;
  private readonly fillWithStrokeColorCount: number = 0;
  private readonly strokeWithFillColorCount: number = 0;

  private onChangeCallbacks = new Set<OnChangeCallback>();

  /**
   * A cache for storing computed properties associated with `Graphic` instances.
   */
  private static graphicCache = new GraphicLruCache(50);

  constructor(params: GraphicParams) {
    this.naturalSize = params.naturalSize;
    this.naturalAspectRatio = this.naturalSize[0] / this.naturalSize[1];

    this.fills = [];
    this.strokes = [];
    const counts = this.addFillsAndStrokes(params.content);
    this.fillCount = counts.fillCount;
    this.fillWithStrokeColorCount = counts.fillWithStrokeColorCount;
    this.strokeWithFillColorCount = counts.strokeWithFillColorCount;

    this.textArea = new GraphicTextArea(
      params.content.textArea?.topLeft ?? GraphicPoint.ZERO,
      params.content.textArea?.bottomRight ?? GraphicPoint.UNIT,
    );
  }

  addFillsAndStrokes(contents: Pick<GraphicContent, 'fills' | 'strokes'>) {
    let fillCount = 0;
    let fillWithStrokeColorCount = 0;
    let strokeWithFillColorCount = 0;

    for (const fillDef of contents.fills) {
      const fill = new GraphicFillSection({
        color: colorDefinitionToColor(fillDef.color),
        fillRule: fillDef.fillRule,
      });
      for (const segmentDef of fillDef.segments) {
        fill.push(segmentDefinitionToSegment(segmentDef));
      }
      this.fills.push(fill);

      if (fill.color instanceof GraphicStrokeColor) {
        fillWithStrokeColorCount++;
      } else {
        fillCount++;
      }
    }

    for (const strokeDef of contents.strokes) {
      const stroke = new GraphicSection({
        color: colorDefinitionToColor(strokeDef.color),
      });
      for (const segmentDef of strokeDef.segments) {
        stroke.push(segmentDefinitionToSegment(segmentDef));
      }
      this.strokes.push(stroke);

      if (stroke.color instanceof GraphicFillColor) {
        strokeWithFillColorCount++;
      }
    }

    return { fillCount, fillWithStrokeColorCount, strokeWithFillColorCount };
  }

  /**
   * Whether the graphic has any fills that need to be rendered
   * This excludes fills with stroke color as they need to behave as a "stroke"
   *
   * @returns Whether the graphic has any fills
   */
  hasFills() {
    return this.fillCount > 0;
  }

  /**
   * Registers a function to be called whenever this graphic's data is updated.
   *
   * @param callback a function to be called when the graphic's data is updated.
   * @returns a cleanup function that removes the current subscription
   */
  onChange(callback: OnChangeCallback): () => void {
    this.onChangeCallbacks.add(callback);
    return () => this.onChangeCallbacks.delete(callback);
  }

  /**
   * Calls all registered onChange callbacks, allowing subscribers to update
   * to use the updated graphics data.
   */
  sendChange() {
    for (const subscriber of this.onChangeCallbacks) {
      subscriber(this);
    }
  }

  /**
   * Returns an array of Path2D objects that each represent a fill of the
   * graphic, i.e. a 1:1 relationship.
   * @param size The size of the graphic in world units
   * @param rotation The rotation of the graphic in radians (optional)
   * @param relativeRotationOrigin The origin point of the rotation in world scale,
   * relative to the top left of the graphic (optional)
   * @returns An array of Path2D objects.
   */
  getPath2DArray(
    size: ReadonlyVec2,
    rotation?: number,
    relativeRotationOrigin?: ReadonlyVec2,
  ): ReadonlyArray<Path2D> {
    const cacheKey = 'path2dArray-' + size[0] + 'x' + size[1];

    return Graphic.graphicCache.getOrInsertProperty<ReadonlyArray<Path2D>>(
      this,
      cacheKey,
      () =>
        this.fills
          .map((fill) =>
            graphicFillToPath2D(fill, size, rotation, relativeRotationOrigin),
          )
          .filter((path): path is Path2D => path !== null),
    );
  }

  /**
   * Returns a generator that yields lines representing strokes of the graphic,
   * i.e. a 1:1 relationship. Each line is defined in world-space.
   * More performant than an array for hit testing scenarios.
   * @returns A generator that yields lines.
   */
  *getStrokesAsLines(): IterableIterator<GraphicLine> {
    for (const stroke of this.strokes) {
      yield* getSectionAsLines(this, stroke);
    }
  }

  /**
   * Determines if *any* fill in this graphic is defined with a GraphicStrokeColor.
   */
  hasFillsWithStrokeColor(): boolean {
    return this.fillWithStrokeColorCount > 0;
  }

  /**
   * Determines if *any* stroke in this graphic is defined with a GraphicFillColor.
   */
  hasStrokesWithFillColor(): boolean {
    return this.strokeWithFillColorCount > 0;
  }
}

class GraphicTextArea {
  readonly sizeFromShapeSize: ReadonlyGraphicPoint;
  readonly shapeSizeFromSize: ReadonlyGraphicPoint;
  readonly centerOffsetFromShapeSize: ReadonlyGraphicPoint;

  constructor(
    readonly topLeft: ReadonlyGraphicPoint,
    readonly bottomRight: ReadonlyGraphicPoint,
  ) {
    // We calculate these once only as the way they change with the size of the
    // shape is encoded into a GraphicPoint
    this.sizeFromShapeSize = GraphicPoint.subtract(
      GraphicPoint.create(),
      bottomRight,
      topLeft,
    );
    this.shapeSizeFromSize = GraphicPoint.inverse(
      GraphicPoint.create(),
      this.sizeFromShapeSize,
    );
    this.centerOffsetFromShapeSize = this.getCenter();
  }

  private getCenter(): ReadonlyGraphicPoint {
    // Take the midpoint of the two corners
    let offset = GraphicPoint.interpolate(
      GraphicPoint.create(),
      this.topLeft,
      this.bottomRight,
      0.5,
    ); // center of content in 0 -> 1 space from top-left of shape

    // Move basis for offset from top-left corner of shape to center of shape
    offset = GraphicPoint.subtract(offset, offset, GraphicPoint.HALF_UNIT); // center of content in 0 -> 1 space from center of shape

    return offset;
  }
}
